import { RefObject, useCallback, useEffect, useState } from "react";

import type { OrgChartData } from "./types";

import { computeDefaultExpanded, getAncestors } from "./expandCollapse";

interface UseChartExpansionOptions {
	fullOrgData: OrgChartData;
	childrenMap: Map<string, string[]>;
	parentMap: Map<string, string>;
	fitOnNextRef: RefObject<boolean>;
}

export function useChartExpansion({ fullOrgData, childrenMap, parentMap, fitOnNextRef }: UseChartExpansionOptions) {
	const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

	useEffect(() => {
		const expandAllByDefault = fullOrgData.employees.length > 0 && fullOrgData.employees.length <= 200;
		setExpandedIds(computeDefaultExpanded(fullOrgData, childrenMap, expandAllByDefault));
		fitOnNextRef.current = true;
	}, [fullOrgData, childrenMap, fitOnNextRef]);

	const expandToNode = useCallback(
		(nodeId: string) => {
			const ancestors = getAncestors(nodeId, parentMap);
			if (ancestors.length === 0) return;
			setExpandedIds((previous) => {
				const next = new Set(previous);
				for (const ancestorId of ancestors) {
					next.add(ancestorId);
				}
				return next;
			});
		},
		[parentMap]
	);

	const expandAll = useCallback(() => {
		fitOnNextRef.current = true;
		const all = new Set<string>();
		for (const [parentId] of childrenMap) {
			all.add(parentId);
		}
		setExpandedIds(all);
	}, [childrenMap, fitOnNextRef]);

	const collapseAll = useCallback(() => {
		fitOnNextRef.current = true;
		setExpandedIds(new Set());
	}, [fitOnNextRef]);

	return { expandedIds, setExpandedIds, expandToNode, expandAll, collapseAll };
}
